
/**
 * File: src/runtime/npc/npcStateMachine.ts
 *
 * NPC Runtime State Machine (A3-safe)
 * - Pure compute (no side effects)
 * - No reducer writes
 * - Compatible with TimelineRange.probs + TimelineRange.q from timeline.types.ts
 *
 * Exports are aligned to npcRuntimeLoop.ts:
 *  - computeNpcRuntime(...)
 *  - NpcRuntimeSnapshot
 *  - TimelineRangeLike
 */

export type NpcBucket = "attention" | "emotion" | "overload" | "safety";
export type NpcCombineModel = "born" | "softmax";

export type TimelineRangeLike = {
  id: string;
  startSec: number;
  endSec: number;
  trackId?: string;
  kind?:
    | "attention"
    | "overload"
    | "emotion"
    | "music"
    | "selection"
    | "loop"
    | "safety"
    | string;

  probs?: Partial<Record<NpcBucket, number>>;
  q?: {
    model?: NpcCombineModel;
    temperature?: number;
    phaseBias?: number;
  };
};

export type ComputeNpcRuntimeInput = {
  playheadSec: number;
  ranges: ReadonlyArray<TimelineRangeLike>;
  npcTrackId?: string;
  windowSec?: number;
  qDefault?: TimelineRangeLike["q"];
  debug?: boolean;
};

export type NpcRuntimeSnapshot = {
  mode: NpcBucket;
  probs: Record<NpcBucket, number>;
  activeRangeIds: string[];
  ts: number;
  playheadSec: number;
  npcTrackId: string;

  debug?: {
    windowSec: number;
    model: NpcCombineModel;
    temperature: number;
    contributors: Array<{
      id: string;
      startSec: number;
      endSec: number;
      trackId?: string;
      probs: Partial<Record<NpcBucket, number>>;
      q?: TimelineRangeLike["q"];
    }>;
  };
};

const BUCKETS: readonly NpcBucket[] = ["attention", "emotion", "overload", "safety"] as const;

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function clamp01(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return 0;
  return clamp(n, 0, 1);
}

function finiteOr(v: unknown, fallback: number): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function overlaps(a0: number, a1: number, b0: number, b1: number): boolean {
  const s0 = Math.min(a0, a1);
  const e0 = Math.max(a0, a1);
  const s1 = Math.min(b0, b1);
  const e1 = Math.max(b0, b1);
  // inclusive-ish overlap; avoids flicker when edges kiss
  return e0 >= s1 && e1 >= s0;
}

function normalizeQ(q?: TimelineRangeLike["q"]): Required<NonNullable<TimelineRangeLike["q"]>> {
  const model: NpcCombineModel = q?.model === "softmax" ? "softmax" : "born";
  const temperature = clamp(finiteOr(q?.temperature, 1), 0.05, 10);
  const phaseBias = finiteOr(q?.phaseBias, 0);
  return { model, temperature, phaseBias };
}

function dominantBucket(p: Record<NpcBucket, number>): NpcBucket {
  // Deterministic tie-breaker: BUCKETS order
  let best: NpcBucket = BUCKETS[0];
  let bestV = p[best];

  for (const b of BUCKETS) {
    const v = p[b];
    if (v > bestV) {
      best = b;
      bestV = v;
    }
  }
  return best;
}

/**
 * born model: independent per-bucket noisy-OR:
 *   out_b = 1 - Π(1 - p_i_b)
 * Not normalized across buckets (by design).
 */
function combineBorn(ranges: ReadonlyArray<TimelineRangeLike>): Record<NpcBucket, number> {
  const out: Record<NpcBucket, number> = {
    attention: 0,
    emotion: 0,
    overload: 0,
    safety: 0,
  };

  for (const b of BUCKETS) {
    let prod = 1;
    for (const r of ranges) {
      const p = clamp01(r.probs?.[b]);
      prod *= 1 - p;
    }
    out[b] = clamp01(1 - prod);
  }

  return out;
}

/**
 * softmax model:
 * - Accumulate evidence per bucket via logit(p) + phaseBias
 * - Convert to normalized distribution via softmax(score / temperature)
 *
 * Note: if a range lacks probs[b], it's treated as 0.
 */
function combineSoftmax(
  ranges: ReadonlyArray<TimelineRangeLike>,
  qDefault: Required<NonNullable<TimelineRangeLike["q"]>>
): Record<NpcBucket, number> {
  const eps = 1e-6;
  const scores: Record<NpcBucket, number> = {
    attention: 0,
    emotion: 0,
    overload: 0,
    safety: 0,
  };

  // Temperature is allowed to be set on any contributing range; first one wins.
  // Otherwise fallback to qDefault.
  let temp = qDefault.temperature;

  for (const r of ranges) {
    const q = normalizeQ({ ...qDefault, ...(r.q ?? {}) });
    if (r.q?.temperature != null) temp = q.temperature;

    for (const b of BUCKETS) {
      const pRaw = clamp01(r.probs?.[b]);
      const p = clamp(pRaw, eps, 1 - eps);
      const logit = Math.log(p / (1 - p));
      scores[b] += logit + q.phaseBias;
    }
  }

  const t = clamp(temp, 0.05, 10);

  // Softmax normalization
  const scaled = BUCKETS.map((b) => scores[b] / t);
  const maxV = Math.max(...scaled);
  const exps = scaled.map((v) => Math.exp(v - maxV));
  const sum = exps.reduce((a, b) => a + b, 0) || 1;

  return {
    attention: exps[0] / sum,
    emotion: exps[1] / sum,
    overload: exps[2] / sum,
    safety: exps[3] / sum,
  };
}

/**
 * Public entrypoint expected by npcRuntimeLoop.ts
 */
export function computeNpcRuntime(input: ComputeNpcRuntimeInput): NpcRuntimeSnapshot {
  const ts = Date.now();
  const npcTrackId = String(input.npcTrackId ?? "n1");
  const windowSec = Math.max(0, finiteOr(input.windowSec, 0.25));

  const qDefault = normalizeQ(input.qDefault);

  const playMin = input.playheadSec - windowSec;
  const playMax = input.playheadSec + windowSec;

  const contributors = input.ranges
    .filter((r) => r && typeof r.id === "string")
    .filter((r) => Number.isFinite(r.startSec) && Number.isFinite(r.endSec))
    .filter((r) => (npcTrackId ? String(r.trackId ?? "") === npcTrackId : true))
    .filter((r) => overlaps(r.startSec, r.endSec, playMin, playMax));

  // Model selection:
  // - If any contributor explicitly requests softmax, use softmax.
  // - Else default to born.
  const model: NpcCombineModel =
    contributors.some((r) => r.q?.model === "softmax") ? "softmax" : "born";

  const probs =
    model === "softmax"
      ? combineSoftmax(contributors, qDefault)
      : combineBorn(contributors);

  const mode = dominantBucket(probs);

  const snapshot: NpcRuntimeSnapshot = {
    mode,
    probs,
    activeRangeIds: contributors.map((r) => r.id),
    ts,
    playheadSec: input.playheadSec,
    npcTrackId,
  };

  if (input.debug) {
    // Choose a representative temperature to expose
    const temp =
      contributors.find((r) => typeof r.q?.temperature === "number")?.q?.temperature ??
      qDefault.temperature;

    snapshot.debug = {
      windowSec,
      model,
      temperature: clamp(finiteOr(temp, 1), 0.05, 10),
      contributors: contributors.map((r) => ({
        id: r.id,
        startSec: r.startSec,
        endSec: r.endSec,
        trackId: r.trackId,
        probs: r.probs ?? {},
        q: r.q,
      })),
    };
  }

  return snapshot;
}
